import React, { useMemo, useState } from 'react';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { IncidentComs } from 'components/IAP/hooks/incidentComsHooks';
import { COMS_TYPES } from 'components/IAP/ICSConstants';

import { ICS204_TABLE } from './AssignmentList/assignmentCardStyles';

export type EditableComsFields = Pick<
  IncidentComs,
  | 'channel_number'
  | 'function'
  | 'channel_name'
  | 'rx_frequency'
  | 'rx_tone_nac'
  | 'tx_frequency'
  | 'tx_tone_nac'
  | 'mode'
  | 'remarks'
  | 'work_group_id'
  | 'cell_phone_number'
  | 'other'
> & {
  type?: string;
};

type IncidentComsTableProps = {
  rows: IncidentComs[];
  workGroups: { id: string; name: string }[];
  onCreate: (fields: EditableComsFields) => Promise<void>;
  onUpdate: (row: IncidentComs, fields: EditableComsFields) => Promise<void>;
  onDelete: (row: IncidentComs) => Promise<void>;
  isSaving?: boolean;
  readOnly?: boolean;
};

type TextField = Exclude<keyof EditableComsFields, 'type' | 'work_group_id'>;

/**
 * One column of box 4, in printed order.
 *
 * A Nonradio row is a phone number rather than a frequency, so the two kinds of
 * row use disjoint halves of the same table. A cell the row's kind does not use
 * shows an em dash rather than an empty input, which is what stops someone
 * typing a talk group onto a contact.
 */
type ComsColumn =
  | {
      kind: 'text';
      field: TextField;
      header: string;
      radioOnly?: boolean;
      nonradioOnly?: boolean;
      placeholder?: string;
      nonradioPlaceholder?: string;
      truncate?: boolean;
    }
  | { kind: 'workGroup'; header: string };

const COMS_COLUMNS: ComsColumn[] = [
  {
    kind: 'text',
    field: 'channel_number',
    header: 'Channel Number',
    radioOnly: true,
  },
  { kind: 'text', field: 'function', header: 'Function', radioOnly: true },
  {
    kind: 'text',
    field: 'channel_name',
    header: 'Channel name / Trunked Radio System Talk Group',
    nonradioPlaceholder: 'Contact name',
  },
  { kind: 'workGroup', header: 'Assignment (Work Group)' },
  {
    kind: 'text',
    field: 'rx_frequency',
    header: 'RX Freq N or W',
    radioOnly: true,
  },
  {
    kind: 'text',
    field: 'rx_tone_nac',
    header: 'RX Tone/NAC',
    radioOnly: true,
  },
  {
    kind: 'text',
    field: 'tx_frequency',
    header: 'TX Freq N or W',
    radioOnly: true,
  },
  {
    kind: 'text',
    field: 'tx_tone_nac',
    header: 'TX Tone/NAC',
    radioOnly: true,
  },
  { kind: 'text', field: 'mode', header: 'Mode (A, D, M)', radioOnly: true },
  { kind: 'text', field: 'remarks', header: 'Remarks', truncate: true },
  {
    kind: 'text',
    field: 'cell_phone_number',
    header: 'Cell phone',
    nonradioOnly: true,
    placeholder: 'Cell',
  },
  {
    kind: 'text',
    field: 'other',
    header: 'Other',
    nonradioOnly: true,
    placeholder: 'Other',
  },
];

const defaultEditableValues: EditableComsFields = {
  type: 'Radio',
  channel_number: '',
  function: '',
  channel_name: '',
  work_group_id: '',
  rx_frequency: '',
  rx_tone_nac: '',
  tx_frequency: '',
  tx_tone_nac: '',
  mode: '',
  remarks: '',
  cell_phone_number: '',
  other: '',
};

/**
 * Base UI's Select has no empty-string value, so the cleared state needs a
 * token.
 */
const NO_WORK_GROUP = '__none__';

const isNonradio = (type: string | undefined) =>
  (type ?? '').toLowerCase() === 'nonradio';

/** Whether this column applies to a row of the given kind. */
const columnApplies = (column: ComsColumn, type: string | undefined) => {
  if (column.kind !== 'text') return true;
  if (column.radioOnly && isNonradio(type)) return false;
  if (column.nonradioOnly && !isNonradio(type)) return false;
  return true;
};

const IncidentComsTable: React.FC<IncidentComsTableProps> = ({
  rows,
  workGroups,
  onCreate,
  onUpdate,
  onDelete,
  isSaving = false,
  readOnly = false,
}) => {
  const [pendingDelete, setPendingDelete] = useState<IncidentComs | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [newRow, setNewRow] = useState<EditableComsFields>({
    ...defaultEditableValues,
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDrafts, setEditDrafts] = useState<
    Record<string, EditableComsFields>
  >({});
  const [searchTerm, setSearchTerm] = useState('');
  const [addRowError, setAddRowError] = useState<string | null>(null);

  const handleNewRowFieldChange = (
    field: keyof EditableComsFields,
    value: string
  ) => {
    setNewRow((prev) => ({ ...prev, [field]: value }));
    if (addRowError) setAddRowError(null);
  };

  const handleEditFieldChange = (
    id: string,
    field: keyof EditableComsFields,
    value: string
  ) => {
    setEditDrafts((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const startAddRow = () => {
    setNewRow({ ...defaultEditableValues });
    setAddRowError(null);
    setIsAdding(true);
  };

  const cancelAddRow = () => {
    setIsAdding(false);
    setNewRow({ ...defaultEditableValues });
    setAddRowError(null);
  };

  const requiredFieldsMessageRadio =
    'Channel name, RX frequency, and TX frequency are required for Radio.';
  const requiredFieldsMessageNonradio =
    'Channel name is required for Nonradio.';

  const handleCreate = async () => {
    const trimmed = { ...newRow, type: newRow.type ?? 'Radio' };

    if (isNonradio(trimmed.type)) {
      if (!trimmed.channel_name?.trim()) {
        setAddRowError(requiredFieldsMessageNonradio);
        return;
      }
    } else if (
      !trimmed.channel_name?.trim() ||
      !trimmed.rx_frequency?.trim() ||
      !trimmed.tx_frequency?.trim()
    ) {
      setAddRowError(requiredFieldsMessageRadio);
      return;
    }

    setAddRowError(null);
    await onCreate(trimmed);

    setIsAdding(false);
    setNewRow({ ...defaultEditableValues });
  };

  const startEditRow = (row: IncidentComs) => {
    setEditingId(row.id);
    setEditDrafts((prev) => ({
      ...prev,
      [row.id]: {
        type: row.type,
        channel_number: row.channel_number || '',
        function: row.function || '',
        channel_name: row.channel_name || '',
        work_group_id: row.work_group_id || '',
        rx_frequency: row.rx_frequency || '',
        rx_tone_nac: row.rx_tone_nac || '',
        tx_frequency: row.tx_frequency || '',
        tx_tone_nac: row.tx_tone_nac || '',
        mode: row.mode || '',
        remarks: row.remarks || '',
        cell_phone_number: row.cell_phone_number || '',
        other: row.other || '',
      },
    }));
  };

  const cancelEditRow = (id: string) => {
    if (editingId === id) setEditingId(null);
  };

  const handleSaveEdit = async (row: IncidentComs) => {
    const draft = editDrafts[row.id];
    if (!draft) return;
    await onUpdate(row, { ...draft, type: draft.type ?? row.type ?? 'Radio' });
    setEditingId(null);
  };

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => {
      const haystack = [
        row.channel_number,
        row.function,
        row.channel_name,
        row.work_group_name,
        row.work_group_id,
        row.rx_frequency,
        row.tx_frequency,
        row.mode,
        row.remarks,
        row.cell_phone_number,
        row.other,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [rows, searchTerm]);

  const displayType = (type: string | undefined) =>
    isNonradio(type) ? 'Nonradio' : 'Radio';

  const workGroupName = (workGroupId: string | undefined) =>
    workGroups.find((group) => group.id === workGroupId)?.name;

  const columnCount = COMS_COLUMNS.length + (readOnly ? 1 : 2);

  const renderWorkGroupSelect = (
    value: string,
    onChange: (next: string) => void,
    label: string
  ) => (
    <Select
      value={value || NO_WORK_GROUP}
      onValueChange={(next) =>
        onChange(next === NO_WORK_GROUP ? '' : String(next))
      }
    >
      <SelectTrigger aria-label={label} className="tw:min-w-40">
        <SelectValue placeholder="Select..." />
      </SelectTrigger>
      <SelectContent className="dark">
        <SelectItem value={NO_WORK_GROUP}>None</SelectItem>
        {workGroups.map((group) => (
          <SelectItem key={group.id} value={group.id}>
            {group.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  /** A cell of the add row, or of a row being edited. */
  const renderEditableCell = (
    column: ComsColumn,
    draft: EditableComsFields,
    onChange: (field: keyof EditableComsFields, value: string) => void,
    rowLabel: string
  ): React.ReactNode => {
    if (!columnApplies(column, draft.type)) {
      return <span className="tw:text-muted-foreground">—</span>;
    }
    if (column.kind === 'workGroup') {
      return renderWorkGroupSelect(
        draft.work_group_id ?? '',
        (next) => onChange('work_group_id', next),
        `${column.header} for ${rowLabel}`
      );
    }
    return (
      <Input
        className="tw:min-w-28"
        aria-label={`${column.header} for ${rowLabel}`}
        placeholder={
          isNonradio(draft.type)
            ? (column.nonradioPlaceholder ?? column.placeholder)
            : column.placeholder
        }
        value={(draft[column.field] as string) ?? ''}
        onChange={(event) => onChange(column.field, event.target.value)}
      />
    );
  };

  const renderReadCell = (
    column: ComsColumn,
    row: IncidentComs
  ): React.ReactNode => {
    if (!columnApplies(column, row.type)) {
      return <span className="tw:text-muted-foreground">—</span>;
    }
    if (column.kind === 'workGroup') {
      return row.work_group_name || workGroupName(row.work_group_id) || '—';
    }
    const value = (row[column.field] as string | undefined) ?? '';
    if (column.truncate) {
      return (
        <span title={value} className="tw:inline-block tw:max-w-50 tw:truncate">
          {value}
        </span>
      );
    }
    return value;
  };

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        <Input
          className="tw:max-w-xs"
          aria-label="Search channels"
          placeholder="Search channel..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        {!readOnly && (
          <Button
            type="button"
            size="sm"
            className="tw:ml-auto"
            disabled={isAdding}
            onClick={startAddRow}
          >
            <Plus className="tw:size-4" />
            Add Channel
          </Button>
        )}
      </div>

      <div className="tw:overflow-x-auto">
        <Table className={ICS204_TABLE} data-testid="ics205-channels">
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              {COMS_COLUMNS.map((column) => (
                <TableHead key={column.header}>{column.header}</TableHead>
              ))}
              {!readOnly && <TableHead className="tw:w-24" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {!readOnly && isAdding && addRowError && (
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  className="tw:text-destructive"
                >
                  {addRowError}
                </TableCell>
              </TableRow>
            )}

            {!readOnly && isAdding && (
              <TableRow data-testid="ics205-new-channel-row">
                <TableCell>
                  <Select
                    value={newRow.type ?? 'Radio'}
                    onValueChange={(next) =>
                      handleNewRowFieldChange('type', String(next))
                    }
                  >
                    <SelectTrigger
                      aria-label="Type for the new channel"
                      className="tw:min-w-32"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark">
                      {COMS_TYPES.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                {COMS_COLUMNS.map((column) => (
                  <TableCell key={column.header}>
                    {renderEditableCell(
                      column,
                      newRow,
                      handleNewRowFieldChange,
                      'the new channel'
                    )}
                  </TableCell>
                ))}
                <TableCell>
                  <div className="tw:flex tw:justify-end tw:gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Cancel the new channel"
                      onClick={cancelAddRow}
                    >
                      <X className="tw:size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Save the new channel"
                      disabled={isSaving}
                      onClick={() => void handleCreate()}
                    >
                      <Check className="tw:size-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )}

            {filteredRows.length === 0 && !isAdding ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="tw:text-center">
                  {searchTerm
                    ? 'No results!'
                    : 'No communication channels configured'}
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((row) => {
                const draft = editDrafts[row.id];
                const isEditing = editingId === row.id && draft != null;
                const rowLabel = row.channel_name || 'this channel';

                return (
                  <TableRow key={row.id}>
                    {/* Type decides which half of the row applies, so changing
                        it would silently blank cells. It is set once, on add. */}
                    <TableCell>{displayType(row.type)}</TableCell>
                    {COMS_COLUMNS.map((column) => (
                      <TableCell key={column.header}>
                        {isEditing
                          ? renderEditableCell(
                              column,
                              draft,
                              (field, value) =>
                                handleEditFieldChange(row.id, field, value),
                              rowLabel
                            )
                          : renderReadCell(column, row)}
                      </TableCell>
                    ))}
                    {!readOnly && (
                      <TableCell>
                        <div className="tw:flex tw:justify-end tw:gap-1">
                          {isEditing ? (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Cancel editing ${rowLabel}`}
                                onClick={() => cancelEditRow(row.id)}
                              >
                                <X className="tw:size-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Save ${rowLabel}`}
                                disabled={isSaving}
                                onClick={() => void handleSaveEdit(row)}
                              >
                                <Check className="tw:size-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Edit ${rowLabel}`}
                                onClick={() => startEditRow(row)}
                              >
                                <Pencil className="tw:size-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Delete ${rowLabel}`}
                                onClick={() => setPendingDelete(row)}
                              >
                                <Trash2 className="tw:size-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <span className="tw:text-sm tw:text-muted-foreground">
        {filteredRows.length}{' '}
        {filteredRows.length === 1 ? 'Channel' : 'Channels'}
      </span>

      <AlertDialog
        open={pendingDelete != null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
      >
        <AlertDialogContent
          className="dark"
          data-testid="ics205-delete-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this channel?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes{' '}
              {pendingDelete?.channel_name?.trim() || 'this channel'} from the
              ICS-205 and from any assignment that cites it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ics205-delete-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="ics205-delete-confirm-accept"
              disabled={isSaving}
              onClick={() => {
                const row = pendingDelete;
                setPendingDelete(null);
                if (row) void onDelete(row);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default IncidentComsTable;
